import type { JSX } from "preact";
import { useEffect, useRef } from "preact/hooks";
import { useSignal } from "@preact/signals";

import { workspace } from "../model/WorkspaceStore.js";
import type { LoadedArtifact } from "../model/types.js";
import {
  SEARCH_TARGET_TYPES,
  type SearchMatchRange,
  type SearchResult,
  type SearchTargetType,
  type SearchUsage,
} from "../search/SearchIndexProtocol.js";

import styles from "./QuickSearch.module.css";

const RESULT_ROW_HEIGHT = 30;
const SEARCH_DEBOUNCE_MS = 40;
const MAX_RESULT_LIST_HEIGHT = 440;
const EMPTY_RESULT_LIST_HEIGHT = 92;
const VIRTUAL_OVERSCAN = 6;

const TARGET_LABELS: Readonly<Record<SearchTargetType, string>> = {
  type: "Type",
  constructor: "Constructor",
  stringConstant: "String Constant",
  field: "Field",
  method: "Method",
  javaModule: "Java Module",
};

interface QuickSearchProps {
  readonly artifacts: readonly LoadedArtifact[];
  readonly onClose: () => void;
  readonly onOpenResult: (result: SearchResult) => void;
}

interface HighlightedSymbolProps {
  readonly symbol: string;
  readonly ranges: readonly SearchMatchRange[];
}

function HighlightedSymbol({
  symbol,
  ranges,
}: HighlightedSymbolProps) {
  const content: Array<string | JSX.Element> = [];
  let cursor = 0;

  for (const [index, range] of ranges.entries()) {
    const start = Math.max(cursor, Math.min(symbol.length, range.start));
    const end = Math.max(start, Math.min(symbol.length, range.end));
    if (start > cursor) {
      content.push(symbol.slice(cursor, start));
    }
    if (end > start) {
      content.push(<mark key={`match-${index}`}>{symbol.slice(start, end)}</mark>);
    }
    cursor = end;
  }
  if (cursor < symbol.length) {
    content.push(symbol.slice(cursor));
  }

  return <span>{content}</span>;
}

function displayPath(
  result: SearchResult,
  artifactsById: ReadonlyMap<string, LoadedArtifact>,
): string {
  const artifact = artifactsById.get(result.artifactId);
  if (artifact === undefined || artifact.name === result.filePath) {
    return result.filePath;
  }
  return `${artifact.name}!/${result.filePath}`;
}

export function QuickSearch({
  artifacts,
  onClose,
  onOpenResult,
}: QuickSearchProps) {
  const input = useRef<HTMLInputElement>(null);
  const resultList = useRef<HTMLDivElement>(null);
  const searchGeneration = useRef(0);
  const query = useSignal("");
  const selectedTargetTypes = useSignal<ReadonlySet<SearchTargetType>>(
    new Set(SEARCH_TARGET_TYPES),
  );
  const selectedUsages = useSignal<ReadonlySet<SearchUsage>>(
    new Set<SearchUsage>(["declaration"]),
  );
  const results = useSignal<readonly SearchResult[]>([]);
  const loading = useSignal(false);
  const searchError = useSignal<string | null>(null);
  const selectedIndex = useSignal(0);
  const scrollTop = useSignal(0);
  const viewportHeight = useSignal(EMPTY_RESULT_LIST_HEIGHT);

  const targetSelectionKey = SEARCH_TARGET_TYPES.filter((targetType) =>
    selectedTargetTypes.value.has(targetType),
  ).join("|");
  const usageSelectionKey = (["declaration", "reference"] as const)
    .filter((usage) => selectedUsages.value.has(usage))
    .join("|");
  const indexRevision = workspace.searchIndexRevision.value;
  const indexing = workspace.searchIndexingArtifactCount.value > 0;
  const artifactKey = artifacts.map((artifact) => artifact.id).join("|");

  useEffect(() => {
    input.current?.focus();
  }, []);

  useEffect(() => {
    const element = resultList.current;
    if (element === null) {
      return;
    }

    const updateHeight = (): void => {
      viewportHeight.value = element.clientHeight;
    };
    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const generation = ++searchGeneration.current;
    const targetTypes = SEARCH_TARGET_TYPES.filter((targetType) =>
      selectedTargetTypes.value.has(targetType),
    );
    const usages = (["declaration", "reference"] as const).filter((usage) =>
      selectedUsages.value.has(usage),
    );

    searchError.value = null;
    if (query.value.length === 0 || targetTypes.length === 0 || usages.length === 0) {
      results.value = [];
      loading.value = false;
      selectedIndex.value = 0;
      scrollTop.value = 0;
      if (resultList.current !== null) {
        resultList.current.scrollTop = 0;
      }
      return;
    }

    loading.value = true;
    const previousSelectedId = results.value[selectedIndex.value]?.id ?? null;
    const timeout = window.setTimeout(() => {
      void workspace
        .searchSymbols(query.value, { targetTypes, usages })
        .then((response) => {
          if (searchGeneration.current !== generation) {
            return;
          }
          results.value = response.results;
          const retainedIndex =
            previousSelectedId === null
              ? -1
              : response.results.findIndex(
                  (result) => result.id === previousSelectedId,
                );
          selectedIndex.value = retainedIndex >= 0 ? retainedIndex : 0;
          if (retainedIndex < 0) {
            scrollTop.value = 0;
            if (resultList.current !== null) {
              resultList.current.scrollTop = 0;
            }
          }
          loading.value = false;
        })
        .catch((error: unknown) => {
          if (searchGeneration.current !== generation) {
            return;
          }
          results.value = [];
          selectedIndex.value = 0;
          scrollTop.value = 0;
          if (resultList.current !== null) {
            resultList.current.scrollTop = 0;
          }
          searchError.value =
            error instanceof Error ? error.message : String(error);
          loading.value = false;
        });
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timeout);
      if (searchGeneration.current === generation) {
        searchGeneration.current++;
      }
    };
  }, [
    query.value,
    targetSelectionKey,
    usageSelectionKey,
    indexRevision,
    artifactKey,
  ]);

  const setTargetSelected = (
    targetType: SearchTargetType,
    checked: boolean,
  ): void => {
    const updated = new Set(selectedTargetTypes.value);
    if (checked) {
      updated.add(targetType);
    } else {
      updated.delete(targetType);
    }
    selectedTargetTypes.value = updated;
  };

  const setUsageSelected = (usage: SearchUsage, checked: boolean): void => {
    const updated = new Set(selectedUsages.value);
    if (checked) {
      updated.add(usage);
    } else {
      updated.delete(usage);
    }
    selectedUsages.value = updated;
  };

  const ensureVisible = (index: number): void => {
    const element = resultList.current;
    if (element === null) {
      return;
    }
    const top = index * RESULT_ROW_HEIGHT;
    const bottom = top + RESULT_ROW_HEIGHT;
    if (top < element.scrollTop) {
      element.scrollTop = top;
    } else if (bottom > element.scrollTop + element.clientHeight) {
      element.scrollTop = bottom - element.clientHeight;
    }
  };

  const moveSelection = (offset: number): void => {
    if (results.value.length === 0) {
      return;
    }
    const next = Math.max(
      0,
      Math.min(results.value.length - 1, selectedIndex.value + offset),
    );
    selectedIndex.value = next;
    ensureVisible(next);
  };

  const openResult = (result: SearchResult): void => {
    onOpenResult(result);
    onClose();
  };

  const openSelectedResult = (): void => {
    const result = results.value[selectedIndex.value];
    if (result !== undefined) {
      openResult(result);
    }
  };

  const resultListHeight =
    results.value.length === 0
      ? EMPTY_RESULT_LIST_HEIGHT
      : Math.min(
          MAX_RESULT_LIST_HEIGHT,
          results.value.length * RESULT_ROW_HEIGHT,
        );
  const effectiveViewportHeight = Math.max(
    RESULT_ROW_HEIGHT,
    viewportHeight.value,
  );
  const visibleStart = Math.max(
    0,
    Math.floor(scrollTop.value / RESULT_ROW_HEIGHT) - VIRTUAL_OVERSCAN,
  );
  const visibleEnd = Math.min(
    results.value.length,
    Math.ceil(
      (scrollTop.value + effectiveViewportHeight) / RESULT_ROW_HEIGHT,
    ) + VIRTUAL_OVERSCAN,
  );
  const visibleResults = results.value.slice(visibleStart, visibleEnd);
  const artifactsById = new Map(
    artifacts.map((artifact) => [artifact.id, artifact] as const),
  );

  let emptyMessage: string;
  if (query.value.length === 0) {
    emptyMessage = "Search symbols by name.";
  } else if (
    selectedTargetTypes.value.size === 0 ||
    selectedUsages.value.size === 0
  ) {
    emptyMessage = "Select at least one target type and result kind.";
  } else if (searchError.value !== null) {
    emptyMessage = searchError.value;
  } else if (loading.value) {
    emptyMessage = "Searching…";
  } else if (indexing) {
    emptyMessage = "Indexing files… Results will update automatically.";
  } else {
    emptyMessage = "No matching symbols.";
  }

  return (
    <div
      class={styles.backdrop}
      onPointerDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <section
        class={styles.overlay}
        role="dialog"
        aria-modal="true"
        aria-label="Quick Search"
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.preventDefault();
            onClose();
            return;
          }
          if (event.target !== input.current) {
            return;
          }

          switch (event.key) {
            case "ArrowDown":
              event.preventDefault();
              moveSelection(1);
              break;
            case "ArrowUp":
              event.preventDefault();
              moveSelection(-1);
              break;
            case "Enter":
              event.preventDefault();
              openSelectedResult();
              break;
          }
        }}
      >
        <div class={styles.inputRow}>
          <input
            ref={input}
            class={styles.searchInput}
            type="text"
            value={query.value}
            placeholder="Search symbols"
            aria-label="Search symbols"
            spellcheck={false}
            autocomplete="off"
            onInput={(event) => {
              query.value = event.currentTarget.value;
            }}
          />
          {indexing ? (
            <span
              class={styles.indexingSpinner}
              role="status"
              aria-label="Indexing files"
              title="Indexing files"
            />
          ) : null}
        </div>

        <div class={styles.controls}>
          <div class={styles.checkboxRow} aria-label="Search target types">
            <span class={styles.categoryLabel}>Target:</span>
            {SEARCH_TARGET_TYPES.map((targetType) => (
              <label key={targetType} class={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={selectedTargetTypes.value.has(targetType)}
                  onChange={(event) => {
                    setTargetSelected(targetType, event.currentTarget.checked);
                  }}
                />
                <span>{TARGET_LABELS[targetType]}</span>
              </label>
            ))}
          </div>
          <div class={styles.checkboxRow} aria-label="Search result kinds">
            <span class={styles.categoryLabel}>Kind:</span>
            <label class={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={selectedUsages.value.has("declaration")}
                onChange={(event) => {
                  setUsageSelected("declaration", event.currentTarget.checked);
                }}
              />
              <span>Declarations</span>
            </label>
            <label class={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={selectedUsages.value.has("reference")}
                onChange={(event) => {
                  setUsageSelected("reference", event.currentTarget.checked);
                }}
              />
              <span>References</span>
            </label>
          </div>
        </div>

        <div
          ref={resultList}
          class={styles.resultList}
          style={{ height: `${resultListHeight}px` }}
          role="listbox"
          aria-label="Search results"
          onScroll={(event) => {
            scrollTop.value = event.currentTarget.scrollTop;
            viewportHeight.value = event.currentTarget.clientHeight;
          }}
        >
          {results.value.length === 0 ? (
            <div class={styles.emptyResult}>{emptyMessage}</div>
          ) : (
            <div
              class={styles.resultCanvas}
              style={{ height: `${results.value.length * RESULT_ROW_HEIGHT}px` }}
            >
              {visibleResults.map((result, offset) => {
                const index = visibleStart + offset;
                const selected = index === selectedIndex.value;
                const path = displayPath(result, artifactsById);
                return (
                  <button
                    key={result.id}
                    type="button"
                    role="option"
                    aria-selected={selected}
                    class={`${styles.resultItem} ${selected ? styles.resultItemSelected : ""}`}
                    style={{
                      height: `${RESULT_ROW_HEIGHT}px`,
                      transform: `translateY(${index * RESULT_ROW_HEIGHT}px)`,
                    }}
                    title={`${result.symbol}\n${path}`}
                    onPointerEnter={() => {
                      selectedIndex.value = index;
                    }}
                    onClick={() => openResult(result)}
                  >
                    <span class={styles.symbol}>
                      <HighlightedSymbol
                        symbol={result.symbol}
                        ranges={result.matchRanges}
                      />
                    </span>
                    <span class={styles.path}>{path}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
